/**
 * WalletSettings - Modal for managing payment methods
 * Step 16: User Frontend - Wallet Settings Modal
 *
 * Features:
 * - View saved cards with conditional badges
 * - Set primary card for Plan (subscription) payments
 * - Set primary card for Auto Top-up (wallet) payments
 * - Drag-and-drop reordering for backup card priority
 * - Add new cards via shared AddCardForm component
 * - Delete cards (with confirmation, prevents deletion of last card)
 */

import { useState, useEffect, useCallback } from 'react';
import { X, GripVertical, CreditCard, Trash2, Loader2, Plus, AlertCircle } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { AlertModal } from './AlertModal';
import { AddCardForm } from './AddCardForm';

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_primary_subscription: boolean;
  is_primary_wallet: boolean;
  priority_order: number;
}

interface WalletSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  onPaymentMethodsChanged?: () => void;
}

// Card brand display names and colors
const CARD_BRANDS: Record<string, { name: string; color: string }> = {
  visa: { name: 'VISA', color: 'bg-blue-100 text-blue-800' },
  mastercard: { name: 'MC', color: 'bg-orange-100 text-orange-800' },
  amex: { name: 'AMEX', color: 'bg-green-100 text-green-800' },
  discover: { name: 'DISC', color: 'bg-amber-100 text-amber-800' },
  diners: { name: 'DINERS', color: 'bg-gray-100 text-gray-800' },
  jcb: { name: 'JCB', color: 'bg-red-100 text-red-800' },
  unionpay: { name: 'UNION', color: 'bg-cyan-100 text-cyan-800' },
  unknown: { name: 'CARD', color: 'bg-gray-100 text-gray-800' },
};

export function WalletSettings({ isOpen, onClose, locationId, onPaymentMethodsChanged }: WalletSettingsProps) {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [pendingReorder, setPendingReorder] = useState<string[]>([]);
  const [needsCustomerSetup, setNeedsCustomerSetup] = useState(false);

  // Alert modal state
  const [alertModal, setAlertModal] = useState<{ isOpen: boolean; title: string; message: string }>({
    isOpen: false,
    title: '',
    message: '',
  });

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; cardId: string; cardLast4: string }>({
    isOpen: false,
    cardId: '',
    cardLast4: '',
  });

  const showAlert = (title: string, message: string) => {
    setAlertModal({ isOpen: true, title, message });
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, title: '', message: '' });
  };

  // Sort payment methods: primary wallet first, then primary subscription, then backups
  const sortPaymentMethods = (methods: PaymentMethod[]): PaymentMethod[] => {
    return [...methods].sort((a, b) => {
      // Primary wallet always comes first
      if (a.is_primary_wallet && !b.is_primary_wallet) return -1;
      if (b.is_primary_wallet && !a.is_primary_wallet) return 1;

      // Primary subscription comes second (if not also primary wallet)
      if (a.is_primary_subscription && !b.is_primary_subscription) return -1;
      if (b.is_primary_subscription && !a.is_primary_subscription) return 1;

      // Backups maintain their priority order
      return a.priority_order - b.priority_order;
    });
  };

  // Fetch payment methods
  const fetchPaymentMethods = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNeedsCustomerSetup(false);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/payment-methods`,
        { credentials: 'include' }
      );
      const data = await response.json() as {
        success: boolean;
        payment_methods?: PaymentMethod[];
        error?: string;
      };

      if (data.success && data.payment_methods) {
        // Sort with primaries at top on initial load
        const sorted = sortPaymentMethods(data.payment_methods);
        setPaymentMethods(sorted);
        setPendingReorder(sorted.map(m => m.id));
      } else if (data.error?.includes('No Stripe customer')) {
        // No Stripe customer - need to set up billing account first
        setNeedsCustomerSetup(true);
        setShowAddCard(true); // Auto-show AddCardForm which will display BusinessInfoForm
      } else {
        setError(data.error || 'Failed to load payment methods');
      }
    } catch (err) {
      console.error('[WalletSettings] Fetch error:', err);
      setError('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    if (isOpen) {
      fetchPaymentMethods();
      setHasChanges(false);
      setShowAddCard(false);
    }
  }, [isOpen, fetchPaymentMethods]);

  // Handle drag end for reordering
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    if (sourceIndex === destIndex) return;

    const newMethods = Array.from(paymentMethods);
    const [removed] = newMethods.splice(sourceIndex, 1);
    newMethods.splice(destIndex, 0, removed);

    setPaymentMethods(newMethods);
    setPendingReorder(newMethods.map(m => m.id));
    setHasChanges(true);
  };

  // Set card as primary for subscription
  const handleSetPrimarySubscription = async (cardId: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/payment-methods/${cardId}/primary-subscription`,
        {
          method: 'PUT',
          credentials: 'include',
        }
      );
      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        // Update local state and re-sort so primaries appear at top
        setPaymentMethods(prev => {
          const updated = prev.map(m => ({
            ...m,
            is_primary_subscription: m.id === cardId,
          }));
          const sorted = sortPaymentMethods(updated);
          setPendingReorder(sorted.map(m => m.id));
          return sorted;
        });
        onPaymentMethodsChanged?.();
      } else {
        showAlert('Error', data.error || 'Failed to set primary card');
      }
    } catch (err) {
      console.error('[WalletSettings] Set primary subscription error:', err);
      showAlert('Error', 'Failed to set primary card');
    }
  };

  // Set card as primary for top-up
  const handleSetPrimaryTopup = async (cardId: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/payment-methods/${cardId}/primary-topup`,
        {
          method: 'PUT',
          credentials: 'include',
        }
      );
      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        // Update local state and re-sort so primaries appear at top
        setPaymentMethods(prev => {
          const updated = prev.map(m => ({
            ...m,
            is_primary_wallet: m.id === cardId,
          }));
          const sorted = sortPaymentMethods(updated);
          setPendingReorder(sorted.map(m => m.id));
          return sorted;
        });
        onPaymentMethodsChanged?.();
      } else {
        showAlert('Error', data.error || 'Failed to set primary card');
      }
    } catch (err) {
      console.error('[WalletSettings] Set primary topup error:', err);
      showAlert('Error', 'Failed to set primary card');
    }
  };

  // Delete card
  const handleDeleteCard = async (cardId: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/payment-methods/${cardId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );
      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        // Refetch to get updated primary flags (RPC promotes next card to primary)
        await fetchPaymentMethods();
        onPaymentMethodsChanged?.();
      } else {
        showAlert('Error', data.error || 'Failed to delete card');
      }
    } catch (err) {
      console.error('[WalletSettings] Delete error:', err);
      showAlert('Error', 'Failed to delete card');
    } finally {
      setDeleteConfirm({ isOpen: false, cardId: '', cardLast4: '' });
    }
  };

  // Save reorder changes
  const handleSaveChanges = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/payment-methods/reorder`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ordered_ids: pendingReorder }),
        }
      );
      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        setHasChanges(false);
        onPaymentMethodsChanged?.();
        onClose();
      } else {
        showAlert('Error', data.error || 'Failed to save changes');
      }
    } catch (err) {
      console.error('[WalletSettings] Save error:', err);
      showAlert('Error', 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Handle new card added
  const handleCardAdded = () => {
    setShowAddCard(false);
    setNeedsCustomerSetup(false); // Customer was created during card addition
    fetchPaymentMethods();
    onPaymentMethodsChanged?.();
  };

  // Get badge for a card
  const getCardBadge = (card: PaymentMethod, index: number): { text: string; className: string } => {
    if (card.is_primary_subscription && card.is_primary_wallet) {
      return { text: 'Primary (Both)', className: 'bg-gradient-to-r from-blue-100 to-green-100 text-blue-800 border border-blue-300' };
    }
    if (card.is_primary_subscription) {
      return { text: 'Primary (Plan)', className: 'bg-blue-100 text-blue-800' };
    }
    if (card.is_primary_wallet) {
      return { text: 'Primary (Top-up)', className: 'bg-green-100 text-green-800' };
    }

    // Calculate backup number (skip primary cards)
    const primaryCount = paymentMethods.filter(m => m.is_primary_subscription || m.is_primary_wallet).length;
    const backupIndex = index - primaryCount + 1;
    return { text: `Backup #${backupIndex > 0 ? backupIndex : index + 1}`, className: 'bg-gray-100 text-gray-700' };
  };

  // Format expiry date
  const formatExpiry = (month: number, year: number): string => {
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`;
  };

  // Get card brand info
  const getCardBrand = (brand: string): { name: string; color: string } => {
    return CARD_BRANDS[brand.toLowerCase()] || CARD_BRANDS.unknown;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-bold text-gray-900">Wallet Settings</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="flex items-center p-4 bg-red-50 border border-red-200 rounded-md">
              <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          ) : (
            <>
              {/* Legend */}
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Payment Methods</p>
                <p className="text-xs text-gray-500 mb-3">
                  Drag cards to set backup priority order. Use action buttons to set primary cards or delete.
                </p>
                <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 flex items-center justify-center bg-blue-100 border border-blue-400 rounded text-blue-700 font-bold text-[10px]">P</span>
                    <span>Set Plan Primary</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 flex items-center justify-center bg-green-100 border border-green-400 rounded text-green-700 font-bold text-[10px]">T</span>
                    <span>Set Auto Top-up Primary</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 flex items-center justify-center bg-red-50 border border-red-300 rounded text-red-500">
                      <Trash2 className="w-3 h-3" />
                    </span>
                    <span>Delete</span>
                  </div>
                </div>
              </div>

              {/* Card List */}
              {paymentMethods.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No payment methods on file</p>
                </div>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="cards">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="border border-gray-200 rounded-lg divide-y divide-gray-100"
                      >
                        {paymentMethods.map((card, index) => {
                          const badge = getCardBadge(card, index);
                          const brandInfo = getCardBrand(card.brand);

                          return (
                            <Draggable key={card.id} draggableId={card.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`flex items-center gap-3 p-3 bg-white transition-colors ${
                                    snapshot.isDragging ? 'shadow-lg bg-blue-50' : 'hover:bg-gray-50'
                                  }`}
                                >
                                  {/* Drag Handle - hidden for primary cards since they auto-sort to top */}
                                  {card.is_primary_wallet || card.is_primary_subscription ? (
                                    <div className="w-5 h-5" />
                                  ) : (
                                    <div
                                      {...provided.dragHandleProps}
                                      className="cursor-grab text-gray-400 hover:text-gray-600"
                                      title="Drag to reorder backup priority"
                                    >
                                      <GripVertical className="w-5 h-5" />
                                    </div>
                                  )}

                                  {/* Card Brand Icon */}
                                  <div className={`w-10 h-7 flex items-center justify-center rounded text-[10px] font-bold ${brandInfo.color}`}>
                                    {brandInfo.name}
                                  </div>

                                  {/* Card Details */}
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900">
                                      •••• •••• •••• {card.last4}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Expires {formatExpiry(card.exp_month, card.exp_year)}
                                    </div>
                                  </div>

                                  {/* Badge */}
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${badge.className}`}>
                                    {badge.text}
                                  </span>

                                  {/* Action Buttons */}
                                  <div className="flex items-center gap-1">
                                    {/* P button - Set Plan Primary (hidden if already primary) */}
                                    {!card.is_primary_subscription && (
                                      <button
                                        onClick={() => handleSetPrimarySubscription(card.id)}
                                        className="w-7 h-7 flex items-center justify-center bg-blue-100 border border-blue-400 rounded text-blue-700 hover:bg-blue-200 transition-colors font-bold text-xs"
                                        title="Set as Plan Primary"
                                      >
                                        P
                                      </button>
                                    )}

                                    {/* T button - Set Top-up Primary (hidden if already primary) */}
                                    {!card.is_primary_wallet && (
                                      <button
                                        onClick={() => handleSetPrimaryTopup(card.id)}
                                        className="w-7 h-7 flex items-center justify-center bg-green-100 border border-green-400 rounded text-green-700 hover:bg-green-200 transition-colors font-bold text-xs"
                                        title="Set as Auto Top-up Primary"
                                      >
                                        T
                                      </button>
                                    )}

                                    {/* Delete button */}
                                    <button
                                      onClick={() => setDeleteConfirm({ isOpen: true, cardId: card.id, cardLast4: card.last4 })}
                                      className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded text-gray-400 hover:border-red-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                                      title="Remove card"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}

              {/* Add New Card Section */}
              <div className="mt-4">
                {showAddCard ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                    {needsCustomerSetup && (
                      <div className="mb-3 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-2">
                        Please set up your billing account to manage payment methods.
                      </div>
                    )}
                    <AddCardForm
                      locationId={locationId}
                      onSuccess={handleCardAdded}
                      onCancel={needsCustomerSetup ? onClose : () => setShowAddCard(false)}
                      setAsPrimarySubscription={false}
                      setAsPrimaryTopup={false}
                      showCancelButton={!needsCustomerSetup}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAddCard(true)}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    <span className="font-medium">Add New Payment Method</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveChanges}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        onClose={closeAlert}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm.isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Payment Method?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to remove the card ending in <strong>{deleteConfirm.cardLast4}</strong>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm({ isOpen: false, cardId: '', cardLast4: '' })}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCard(deleteConfirm.cardId)}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
